import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Chip,
  Tooltip,
  Switch,
  InputBase,
  Divider,
  Paper,
} from "@mui/material";
import { tooltipClasses } from "@mui/material/Tooltip";
import Grid from "@mui/material/Unstable_Grid2";
import { styled, alpha, keyframes } from "@mui/material/styles";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

// Icons
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import SaveIcon from "@mui/icons-material/Save";
import PrintIcon from "@mui/icons-material/Print";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import FeedbackIcon from "@mui/icons-material/Feedback";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { deleteDocuments } from "../../services/document";
import { addUserLogs } from "../../services/userLogServices";
import { DownloadOptionModal } from "../MyDocumentsCardsModal";
import { useCheckFeatureAccess } from "../CheckFeature";
import { openLeftNavbar } from "../../redux/handleSideNavBarAndModel/handleStatus.action";
import { resetTemplateName } from "../../redux/coauthor/coauthor.action";

// ── Design Tokens ──────────────────────────────────────────────────────────────
const C = {
  surface:      "#FFFFFF",
  surfaceAlt:   "#F8F9FE",
  border:       "rgba(120,140,220,0.18)",
  borderStrong: "rgba(100,120,200,0.28)",
  accent:       "#5B6FE8",
  accentLight:  "#EEF0FD",
  accentMid:    "#C5CCF7",
  text:         "#1E2140",
  textSub:      "#4A5280",
  textMuted:    "#8891B8",
  success:      "#45B26B",
  successLight: "#E8F7EE",
  danger:       "#EF5B5B",
  dangerLight:  "#FDEAEA",
  purple:       "#7C6FD8",
  purpleLight:  "#F0EEFF",
};

const R = { sm: "8px", md: "12px", lg: "16px", xl: "20px", full: "100px" };

const SHADOW = {
  bar:  "0 2px 12px rgba(91,111,232,0.07), 0 1px 3px rgba(0,0,0,0.04)",
  menu: "0 12px 40px rgba(30,33,64,0.14), 0 2px 8px rgba(0,0,0,0.06)",
};

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "blue",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.white,
  },
}));

// ── Styled: Top Bar Wrapper ───────────────────────────────────────────────────
const TopBar = styled(Paper)({
  borderRadius: R.xl,
  background: C.surface,
  border: `1px solid ${C.border}`,
  boxShadow: SHADOW.bar,
  overflow: "hidden",
  marginBottom: "12px",
});

// ── Styled: Title Input ───────────────────────────────────────────────────────
const TitleInput = styled(InputBase)({
  fontSize: "18px",
  fontWeight: 700,
  color: C.text,
  letterSpacing: "-0.02em",
  "& .MuiInputBase-input": {
    padding: "4px 8px",
    borderRadius: R.sm,
    border: `1.5px solid ${C.accentMid}`,
    background: C.accentLight,
    transition: "all 0.15s",
    "&:focus": {
      border: `1.5px solid ${C.accent}`,
      background: C.accentLight,
      boxShadow: `0 0 0 3px ${alpha(C.accent, 0.12)}`,
    },
  },
  minWidth: 200,
  maxWidth: 340,
});

// ── Styled: Menu Button ───────────────────────────────────────────────────────
const MenuBtn = styled(Button)({
  textTransform: "none",
  fontSize: "12.5px",
  fontWeight: 600,
  color: C.textSub,
  padding: "5px 12px",
  borderRadius: R.sm,
  minWidth: "auto",
  border: "1px solid transparent",
  transition: "all 0.14s ease",
  "&:hover": {
    background: C.accentLight,
    color: C.accent,
    border: `1px solid ${C.accentMid}`,
  },
  "&:disabled": {
    color: C.textMuted,
    opacity: 0.5,
  },
});

// ── Styled: Icon Btn ──────────────────────────────────────────────────────────
const IBtn = styled(IconButton)({
  width: 32,
  height: 32,
  borderRadius: R.sm,
  border: `1px solid transparent`,
  color: C.textMuted,
  transition: "all 0.14s ease",
  "&:hover": {
    background: C.accentLight,
    color: C.accent,
    border: `1px solid ${C.accentMid}`,
    transform: "translateY(-1px)",
  },
  "& .MuiSvgIcon-root": { fontSize: 17 },
});

// ── Styled: Custom Toggle Switch ──────────────────────────────────────────────
const ModernSwitch = styled(Switch)(({ theme }) => ({
  width: 44,
  height: 24,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(20px)",
      "& + .MuiSwitch-track": {
        background: `linear-gradient(135deg, ${C.accent} 0%, ${C.purple} 100%)`,
        opacity: 1,
        border: "none",
      },
      "& .MuiSwitch-thumb": {
        background: "#fff",
        boxShadow: "0 2px 6px rgba(91,111,232,0.35)",
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: C.accent,
      border: `6px solid #fff`,
    },
  },
  "& .MuiSwitch-thumb": {
    width: 20,
    height: 20,
    background: "#fff",
    boxShadow: "0 2px 4px rgba(0,0,0,0.14)",
  },
  "& .MuiSwitch-track": {
    borderRadius: 12,
    background: C.border,
    opacity: 1,
    transition: "all 0.3s ease",
  },
}));

// ── Styled: Menu Item ─────────────────────────────────────────────────────────
const SMenuItem = styled(MenuItem)({
  padding: "9px 16px",
  fontSize: "13px",
  fontWeight: 500,
  color: C.text,
  gap: "10px",
  borderRadius: R.sm,
  margin: "1px 6px",
  transition: "all 0.13s ease",
  "&:hover": {
    background: C.accentLight,
    color: C.accent,
    "& .MuiSvgIcon-root": { color: C.accent },
  },
  "& .MuiSvgIcon-root": {
    fontSize: 17,
    color: C.textMuted,
    transition: "color 0.13s",
  },
});

const DangerMenuItem = styled(SMenuItem)({
  "&:hover": {
    background: C.dangerLight,
    color: C.danger,
    "& .MuiSvgIcon-root": { color: C.danger },
  },
});

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const CoAuthorTopMenubar = (props) => {
  const dispatch  = useDispatch();
  const history   = useHistory();
  const { clickState } = useSelector((s) => s.handleClick);
  const currentNavItem = useSelector(
    (s) => s.manageCoauthorNavReducer.currentCoauthorNavItem,
  );

  const [fileMenuAnchor,   setFileMenuAnchor]   = useState(null);
  const [helpMenuAnchor,   setHelpMenuAnchor]   = useState(null);
  const [moreMenuAnchor,   setMoreMenuAnchor]   = useState(null);
  const [deleteOpen,       setDeleteOpen]       = useState(false);
  const [isEditing,        setIsEditing]        = useState(false);
  const [localTitle,       setLocalTitle]       = useState(props.docName || "");
  const [downloadOpen,     setDownloadOpen]     = useState(false);
  const [editModeOn,       setEditModeOn]       = useState(props.selectedEditOption !== false);

  const accessToTitle = useCheckFeatureAccess(currentNavItem, "addTitle");
  const accessToFile  = useCheckFeatureAccess(currentNavItem, "file");

  // Keep local title in sync with prop
  React.useEffect(() => {
    setLocalTitle(props.docName || "");
  }, [props.docName]);

  React.useEffect(() => {
    setEditModeOn(props.selectedEditOption !== false);
  }, [props.selectedEditOption]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleEditClick = () => {
    if (accessToTitle) setIsEditing(true);
  };

  const handleSaveTitle = () => {
    const title = localTitle.trim() || "Untitled Document";
    setLocalTitle(title);
    setIsEditing(false);
    props.setDocName(title);
    props.setUpdateDocName(true);
  };

  const handleCancelEdit = () => {
    setLocalTitle(props.docName || "");
    setIsEditing(false);
  };

  const handleToggleEditMode = (e) => {
    const on = e.target.checked;
    setEditModeOn(on);
    props.handleMenuItemClick?.(on ? "Editor View" : "Viewing");
  };

  const handleCloseMenus = () => {
    setFileMenuAnchor(null);
    setHelpMenuAnchor(null);
    setMoreMenuAnchor(null);
  };

  const handleBackButton = () => {
    if (props.isCoAuthor) history.push("/coauthor");
    else history.goBack();
    dispatch(resetTemplateName(""));
    dispatch(openLeftNavbar());
  };

  const handlePrint = () => {
    if (props.editor?.current) {
      const content = props.editor.current.innerHTML || "";
      const w = window.open("", "_blank", "width=800,height=600");
      w.document.open();
      w.document.write(`
        <html><head><title>Print</title>
        <style>body{font-family:Georgia,serif;margin:32px;line-height:1.75}img{max-width:100%}</style>
        </head><body>${content}</body></html>
      `);
      w.document.close();
      w.onload = () => { w.print(); w.close(); };
    }
    handleCloseMenus();
  };

  const handleExport = () => {
    setDownloadOpen(true);
    handleCloseMenus();
  };

  const handleNewProject = () => {
    handleCloseMenus();
    window.location.reload(true);
  };

  const handleDelete = () => {
    setDeleteOpen(true);
    handleCloseMenus();
  };

  const handleConfirmDelete = async () => {
    const res = await deleteDocuments(props.docId || props.newDocId);
    if (res.status === 200) {
      toast.success("Document deleted!");
      const data = {
        userId: sessionStorage.getItem("userId"),
        organizationId: sessionStorage.getItem("organizationId"),
        actionType: "deleteDoc",
      };
      const addLogs = await addUserLogs(data);
      if (addLogs.status !== 200) toast.error(addLogs.message);
    } else {
      toast.error("Something went wrong!");
    }
    setDeleteOpen(false);
    history.goBack();
  };

  const convertTextToHtml = (text) =>
    text.split("\n").map((p) => `<p>${p.replace(/\n{2,}/g, "<br><br>")}</p>`).join("");

  const handleImport = () => {
    const inp = document.createElement("input");
    inp.type = "file"; inp.style.display = "none";
    inp.addEventListener("change", (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        const text = ev.target.result.toString();
        if (file.name.endsWith(".txt")) {
          props.setEditorContentUpload(props.editorContent + "<br>" + convertTextToHtml(text));
        } else if (file.name.endsWith(".docx")) {
          const match = /<body[^>]*>([\s\S]*?)<\/body>/i.exec(text);
          props.setEditorContentUpload(props.editorContent + "<br>" + (match ? match[1] : ""));
        } else {
          toast.warning("Please upload .txt or .docx file");
        }
      };
      reader.readAsText(file);
    });
    document.body.appendChild(inp);
    inp.click();
    document.body.removeChild(inp);
    handleCloseMenus();
  };

  // ── File menu items ─────────────────────────────────────────────────────────
  const fileItems = props.docId
    ? ["New Project", "Open Project", "Import File", "Export", "Print", "Delete", "Close Project"]
    : ["New Project", "Open Project", "Import File", "Print", "Delete", "Close Project"];

  const fileIcons = {
    "New Project":    <AddIcon />,
    "Open Project":   <FolderOpenIcon />,
    "Import File":    <ImportExportIcon />,
    Export:           <SaveIcon />,
    Print:            <PrintIcon />,
    Delete:           <DeleteOutlineIcon />,
    "Close Project":  <CloseIcon />,
  };

  const fileHandlers = {
    "New Project":   handleNewProject,
    "Open Project":  () => { history.goBack(); handleCloseMenus(); },
    "Import File":   handleImport,
    Export:          handleExport,
    Print:           handlePrint,
    Delete:          handleDelete,
    "Close Project": () => { history.goBack(); handleCloseMenus(); },
  };

  const helpItems = ["Tutorials", "FAQs", "Feedback", "What's New"];
  const helpIcons = {
    Tutorials: <EmojiObjectsIcon />,
    FAQs: <HelpOutlineIcon />,
    Feedback: <FeedbackIcon />,
    "What's New": <NewReleasesIcon />,
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      <TopBar elevation={0}>
        <Grid
          container
          alignItems="center"
          sx={{ px: 2, py: 1.25, minHeight: 56 }}
        >

          {/* ── LEFT: Back + Title ── */}
          <Grid xs="auto" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {!props.isGrammarCheck && !props.trial && !props.mobileView && (
              <LightTooltip title="Back" placement="bottom">
                <IBtn onClick={handleBackButton}>
                  <KeyboardBackspaceIcon />
                </IBtn>
              </LightTooltip>
            )}

            {/* Document Title */}
            {isEditing ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <TitleInput
                  value={localTitle}
                  onChange={(e) => setLocalTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveTitle();
                    if (e.key === "Escape") handleCancelEdit();
                  }}
                  autoFocus
                  placeholder="Document title…"
                  inputProps={{ maxLength: 80 }}
                />
                <LightTooltip title="Save title">
                  <IconButton
                    onClick={handleSaveTitle}
                    size="small"
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: R.sm,
                      background: C.accentLight,
                      color: C.accent,
                      border: `1px solid ${C.accentMid}`,
                      "&:hover": { background: C.accentMid },
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </LightTooltip>
                <LightTooltip title="Cancel">
                  <IconButton
                    onClick={handleCancelEdit}
                    size="small"
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: R.sm,
                      background: C.dangerLight,
                      color: C.danger,
                      border: `1px solid ${alpha(C.danger, 0.25)}`,
                      "&:hover": { background: alpha(C.danger, 0.18) },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </LightTooltip>
              </Box>
            ) : (
              <LightTooltip
                title={accessToTitle ? "Click to rename" : ""}
                placement="bottom"
              >
                <Box
                  onClick={handleEditClick}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    cursor: accessToTitle ? "pointer" : "default",
                    px: 1.25,
                    py: 0.6,
                    borderRadius: R.md,
                    border: "1px solid transparent",
                    transition: "all 0.15s ease",
                    ...(accessToTitle && {
                      "&:hover": {
                        background: C.accentLight,
                        border: `1px solid ${C.accentMid}`,
                        "& .edit-pen": { opacity: 1, transform: "translateX(0)" },
                      },
                    }),
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "15px", md: "17px" },
                      color: C.text,
                      letterSpacing: "-0.02em",
                      maxWidth: { xs: 160, md: 300 },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {clickState === "blog"
                      ? localTitle || "Untitled Blog"
                      : localTitle || "Untitled Document"}
                  </Typography>
                  {accessToTitle && (
                    <AttachFileIcon
                      className="edit-pen"
                      sx={{
                        fontSize: 14,
                        color: C.textMuted,
                        opacity: 0,
                        transform: "translateX(-4px)",
                        transition: "all 0.15s ease",
                        rotate: "45deg",
                      }}
                    />
                  )}
                </Box>
              </LightTooltip>
            )}
          </Grid>

          <Grid xs />

          {/* ── RIGHT: Edit mode toggle + More ── */}
          <Grid
            xs="auto"
            sx={{ display: "flex", alignItems: "center", gap: 1.5, pr: { md: 1 } }}
          >
            {!props.trial && props.circularLoader && props.docName && (
              <Chip
                icon={
                  <CloudDoneIcon sx={{ fontSize: "14px !important", color: `${C.success} !important` }} />
                }
                label="Saved"
                size="small"
                sx={{
                  height: 26,
                  fontSize: "11px",
                  fontWeight: 600,
                  background: C.successLight,
                  color: C.success,
                  border: `1px solid rgba(69,178,107,0.25)`,
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            )}

            {/* View / Edit toggle */}
            {!props.trial && !props.isGrammarCheck && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{
                    fontSize: "11.5px",
                    fontWeight: 600,
                    color: editModeOn ? C.textMuted : C.textSub,
                    transition: "color 0.15s",
                    userSelect: "none",
                  }}
                >
                  View
                </Typography>
                <LightTooltip
                  title={editModeOn ? "Switch to viewing mode" : "Switch to editing mode"}
                  placement="bottom"
                >
                  <ModernSwitch
                    checked={editModeOn}
                    onChange={handleToggleEditMode}
                    size="small"
                  />
                </LightTooltip>
                <Typography
                  sx={{
                    fontSize: "11.5px",
                    fontWeight: 600,
                    color: editModeOn ? C.accent : C.textMuted,
                    transition: "color 0.15s",
                    userSelect: "none",
                  }}
                >
                  Edit
                </Typography>
              </Box>
            )}

            {/* Trial grammar check */}
            {props.trial && (
              <Button
                onClick={() => props.grammarCheck?.()}
                size="small"
                sx={{
                  borderRadius: R.full,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "12px",
                  px: 2,
                  py: 0.7,
                  background: C.accentLight,
                  color: C.accent,
                  border: `1.5px solid ${C.accentMid}`,
                  "&:hover": {
                    background: C.accentMid,
                    color: C.accent,
                  },
                }}
              >
                Check Grammar
              </Button>
            )}

            {/* More menu */}
            <LightTooltip title="More options" placement="bottom">
              <IBtn onClick={(e) => setMoreMenuAnchor(e.currentTarget)}>
                <MoreVertIcon />
              </IBtn>
            </LightTooltip>
          </Grid>
        </Grid>
      </TopBar>

      {/* ── File Menu ── */}
      {/* <Menu
        anchorEl={fileMenuAnchor}
        open={Boolean(fileMenuAnchor)}
        onClose={handleCloseMenus}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 0.75,
            minWidth: 210,
            borderRadius: R.lg,
            border: `1px solid ${C.border}`,
            boxShadow: SHADOW.menu,
            overflow: "visible",
            py: 0.75,
          },
        }}
      >
        {fileItems.map((item) =>
          item === "Delete" ? (
            <DangerMenuItem key={item} onClick={() => fileHandlers[item]?.()}>
              {fileIcons[item]}
              <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>{item}</Typography>
            </DangerMenuItem>
          ) : (
            <SMenuItem key={item} onClick={() => fileHandlers[item]?.()}>
              {fileIcons[item]}
              <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>{item}</Typography>
            </SMenuItem>
          )
        )}
      </Menu> */}

      {/* ── Help Menu ── */}
      {/* <Menu
        anchorEl={helpMenuAnchor}
        open={Boolean(helpMenuAnchor)}
        onClose={handleCloseMenus}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 0.75,
            minWidth: 190,
            borderRadius: R.lg,
            border: `1px solid ${C.border}`,
            boxShadow: SHADOW.menu,
            py: 0.75,
          },
        }}
      >
        {helpItems.map((item) => (
          <SMenuItem key={item} onClick={handleCloseMenus}>
            {helpIcons[item]}
            <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>{item}</Typography>
          </SMenuItem>
        ))}
      </Menu> */}

      {/* ── More Menu ── */}
      <Menu
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={handleCloseMenus}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 0.75,
            minWidth: 200,
            borderRadius: R.lg,
            border: `1px solid ${C.border}`,
            boxShadow: SHADOW.menu,
            py: 0.75,
          },
        }}
      >
        <SMenuItem onClick={handleExport}>
          <SaveIcon />
          <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>Download</Typography>
        </SMenuItem>
        <SMenuItem onClick={handlePrint}>
          <PrintIcon />
          <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>Print</Typography>
        </SMenuItem>
        <Divider sx={{ my: 0.5, borderColor: C.border }} />
        <DangerMenuItem onClick={handleDelete}>
          <DeleteOutlineIcon />
          <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>Delete Document</Typography>
        </DangerMenuItem>
      </Menu>

      {/* ── Delete Confirm Dialog ── */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: R.xl,
            minWidth: { xs: "90%", sm: 400 },
            border: `1px solid ${C.border}`,
            boxShadow: SHADOW.menu,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: R.md,
                background: C.dangerLight,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `1px solid ${alpha(C.danger, 0.25)}`,
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 19, color: C.danger }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: "16px", color: C.text }}>
              Delete Document?
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography sx={{ color: C.textSub, fontSize: "13.5px", lineHeight: 1.7 }}>
            This will permanently delete{" "}
            <Box component="span" sx={{ fontWeight: 700, color: C.text }}>
              "{localTitle || "Untitled Document"}"
            </Box>
            . This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
          <Button
            onClick={() => setDeleteOpen(false)}
            sx={{
              borderRadius: R.md,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "13px",
              px: 2.5,
              border: `1.5px solid ${C.border}`,
              color: C.textSub,
              "&:hover": { background: C.surfaceAlt, borderColor: C.borderStrong },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            sx={{
              borderRadius: R.md,
              textTransform: "none",
              fontWeight: 700,
              fontSize: "13px",
              px: 2.5,
              background: C.danger,
              color: "#fff",
              border: `1.5px solid ${C.danger}`,
              "&:hover": {
                background: "#D94545",
                boxShadow: `0 4px 14px ${alpha(C.danger, 0.35)}`,
                transform: "translateY(-1px)",
              },
              transition: "all 0.15s ease",
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Download Modal ── */}
      <DownloadOptionModal
        show={downloadOpen}
        onHide={() => setDownloadOpen(false)}
        id={props.docId}
      />
    </>
  );
};

export default CoAuthorTopMenubar;
